import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../axios/api.js";

const createAdjustment = (kind = "charge") => ({
  kind,
  label: "",
  calculationType: "amount",
  value: "",
});

const initialState = {
  adjustments: [],
  loading: false,
  error: null,
};

export const fetchQuotationAdjustments = createAsyncThunk(
  "quotationAdjustments/fetchQuotationAdjustments",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/quotation-adjustments/${rfq_no}`);
      return Array.isArray(response.data?.data) ? response.data.data : [];
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch quotation adjustments";
      return rejectWithValue(message);
    }
  }
);

export const saveQuotationAdjustments = createAsyncThunk(
  "quotationAdjustments/saveQuotationAdjustments",
  async ({ rfq_no, adjustments = [] }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put("/api/quotation-adjustments", {
        rfq_no,
        adjustments,
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save quotation adjustments";
      return rejectWithValue(message);
    }
  }
);

const quotationAdjustmentsSlice = createSlice({
  name: "quotationAdjustments",
  initialState,
  reducers: {
    setAdjustments: (state, action) => {
      state.adjustments = Array.isArray(action.payload) ? action.payload : [];
    },
    addAdjustment: (state, action) => {
      const kind = action.payload === "discount" ? "discount" : "charge";
      state.adjustments.push(createAdjustment(kind));
    },
    updateAdjustment: (state, action) => {
      const { index, field, value } = action.payload || {};
      if (typeof index !== "number" || !state.adjustments[index]) return;
      state.adjustments[index][field] = value;
    },
    removeAdjustment: (state, action) => {
      const index = action.payload;
      if (typeof index !== "number") return;
      state.adjustments.splice(index, 1);
    },
    clearAdjustments: (state) => {
      state.adjustments = [];
      state.loading = false;
      state.error = null;
    },
    setAdjustmentsLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    setAdjustmentsError: (state, action) => {
      state.error = action.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotationAdjustments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotationAdjustments.fulfilled, (state, action) => {
        state.loading = false;
        state.adjustments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchQuotationAdjustments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch quotation adjustments";
      })
      .addCase(saveQuotationAdjustments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveQuotationAdjustments.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveQuotationAdjustments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to save quotation adjustments";
      });
  },
});

export const {
  setAdjustments,
  addAdjustment,
  updateAdjustment,
  removeAdjustment,
  clearAdjustments,
  setAdjustmentsLoading,
  setAdjustmentsError,
} = quotationAdjustmentsSlice.actions;

export const selectQuotationAdjustments = (state) =>
  state.quotationAdjustments?.adjustments || [];

export default quotationAdjustmentsSlice.reducer;
